import * as React from 'react';

import {
  Button,
  FilledInput,
  FormControl,
  FormHelperText,
  InputLabel,
} from '@mui/material';
import { requestEmailCode } from '@realadvisor/auth-client';
import { Form, useFormik } from '@realadvisor/form';
import { Box } from 'react-system';

import { useLocale } from '@legacy/hooks/locale';
import { describeAuthError } from '@legacy/routes/Login/authErrors';

type Props = {
  onCodeRequested: (state: { email: string; tenantId: string | null }) => void;
  tenantId: string | null;
};

export const LoginEmailForm = ({ onCodeRequested, tenantId }: Props) => {
  const { t } = useLocale();
  const [requesting, setRequesting] = React.useState(false);
  const form = useFormik({
    initialValues: {
      email: '',
    },
    validate: () => ({}),
    onSubmit: async values => {
      setRequesting(true);
      try {
        const response = await requestEmailCode({
          email: values.email,
          tenantId,
        });
        if (response.ok) {
          onCodeRequested({ email: values.email, tenantId });
          return;
        }
        // 401 means no account carries this address — the one case the user can
        // act on. Everything else is a machine token, mapped rather than shown.
        const detail = await response
          .json()
          .then(body => body.error)
          .catch(() => null);
        form.setResponseErrors({
          email:
            response.status === 401
              ? t('No account found for {{email}}', { email: values.email })
              : describeAuthError(detail, t),
        });
      } catch (err) {
        form.setResponseErrors({
          email: describeAuthError(
            err instanceof Error ? err.message : null,
            t,
          ),
        });
      } finally {
        setRequesting(false);
      }
    },
  });

  return (
    <Form onSubmit={form.submitForm}>
      <Box py={1}>
        <FormControl error={form.errors.email != null}>
          <InputLabel>Email</InputLabel>
          <FilledInput
            type="email"
            value={form.values.email}
            onChange={event => {
              form.setValues({ email: event.target.value });
            }}
          />
          {form.errors.email != null && (
            <FormHelperText>{form.errors.email}</FormHelperText>
          )}
        </FormControl>
      </Box>
      <Box py={1}>
        <Button
          css={{ textAlign: 'center' }}
          variant="contained"
          color="secondary"
          disabled={requesting}
          fullWidth
          onClick={form.submitForm}
        >
          {t('Email me a login code')}
        </Button>
      </Box>
    </Form>
  );
};
